import { ReactNode, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { Link, NavLink } from 'react-router-dom';
import screenfull from 'screenfull';
import ArrowTipDownIcon from '@stripcash/ui-kit/icons/arrowTipDown.svg';
import ArrowTipUpIcon from '@stripcash/ui-kit/icons/arrowTipUp.svg';
import EyeIcon from '@stripcash/ui-kit/icons/eye.svg';
import NotEyeIcon from '@stripcash/ui-kit/icons/notEye.svg';
import FullScreenIcon from '@stripcash/ui-kit/icons/fullScreen.svg';
import FullScreenExitIcon from '@stripcash/ui-kit/icons/fullScreenExit.svg';
import BellIcon from '@stripcash/ui-kit/icons/bell.svg';
import Badge, { BadgeType } from '@stripcash/ui-kit/components/Badge';
import useToggle from '@stripcash/ui-kit/utils/hooks/useToggle';
import { Option } from '@stripcash/ui-kit/components/RadioGroup/types';
import DropdownMenu from '@stripcash/ui-kit/components/DropdownMenu';
import { DropdownMenuSize } from '@stripcash/ui-kit/components/DropdownMenu/types';
import { useTheme } from '@stripcash/ui-kit/hoc/Theme';

import { currentUserMaybeSelector } from 'users/selectors';
import { humanizeMoney } from 'common/helpers/Humanize';
import { OVERVIEW_NEWS_PATH } from 'common/selectors/sidebar/common';
import {
  collapseSidebarAction,
  expandSidebarAction,
} from 'common/reducers/sidebar';
import { Translate } from 'common/i18n/types';
import { useT } from 'common/i18n';
import { isWideMobileRootSelector } from 'common/selectors/ui';
import getAcceptByCookie from 'common/helpers/getAcceptByCookie';
import CookieAccepts from 'common/constants/cookieAccepts';

import { logout as logoutAction } from '../../actions/logout';
import ThemeButton from '../ThemeButton';

import LanguagePopup from './components/LanguagePopup';
import FullLogo from './icons/FullLogo';
import Logo from './icons/Logo';
import s from './styles.module.css';
import { LANGUAGE_POPUP_ID } from './components/LanguagePopup/constants';

export enum HeaderTheme {
  Control = 'control',
  Affiliate = 'affiliate',
  Incarnated = 'incarnated',
}

type HeaderProps = {
  isSidebarShown: boolean;
  expandSidebar: () => void;
  collapseSidebar: () => void;
  logout: () => void;
  username: string;
  news?: number;
  balance?: number;
  showBalance?: boolean;
  dropdownItems?: Option[];
  defaultPath?: string;
  theme?: HeaderTheme;
  onModeChange?: () => void;
  isPrivate: boolean;
  isWideMobile: boolean;
  renderChildren?: (data: {
    t: Translate;
    isWideMobile: boolean;
  }) => ReactNode[];
};

const Header = ({
  isSidebarShown,
  expandSidebar,
  news = 0,
  username = '',
  collapseSidebar,
  logout,
  balance = 0,
  showBalance,
  dropdownItems = [],
  defaultPath = '/',
  theme = HeaderTheme.Control,
  onModeChange,
  isPrivate,
  isWideMobile,
  renderChildren,
}: HeaderProps) => {
  const t = useT('header');
  const { onThemeToggle, currentTheme } = useTheme();
  const [isFullScreen, toggleFullScreen] = useToggle(false);

  const handleFullscreenClick = useCallback(() => {
    screenfull.toggle().then(toggleFullScreen);
  }, []);

  const handleSidebarExpandClick = useCallback(() => {
    if (isSidebarShown) {
      collapseSidebar();
    } else {
      expandSidebar();
    }
  }, [collapseSidebar, expandSidebar, isSidebarShown]);

  const nickname = isPrivate ? `${username.slice(0, 2)}**` : username;

  const currentBalance = useMemo(() => {
    const nextBalance = humanizeMoney(balance, 2, isWideMobile, '$');

    return isPrivate ? String(nextBalance).replace(/\d/g, '*') : nextBalance;
  }, [isWideMobile, balance, isPrivate]);

  const isThemeAvailable = useMemo(
    () => getAcceptByCookie(CookieAccepts.ThemeAvailable),
    [],
  );

  return (
    <header
      id="main-header"
      className={cn(s.root, {
        [s.rootControl]: theme === HeaderTheme.Control,
        [s.rootAff]: theme === HeaderTheme.Affiliate,
        [s.rootIncarnated]: theme === HeaderTheme.Incarnated,
      })}
    >
      <div className={s.burgerMenu}>
        <input
          onChange={handleSidebarExpandClick}
          checked={isSidebarShown}
          className={s.menuToggle}
          id="menu__toggle"
          type="checkbox"
        />
        <label className={s.menuBtn} htmlFor="menu__toggle">
          <span />
        </label>
      </div>
      <div className={s.iconWrapper}>
        <NavLink to={defaultPath} className={s.icon}>
          {isPrivate ? <Logo /> : <FullLogo />}
        </NavLink>
      </div>

      <ul
        className={cn(s.centeredList, {
          [s.centeredListRight]: theme === HeaderTheme.Control,
        })}
      >
        {renderChildren &&
          renderChildren({ t, isWideMobile }).map((node, index) => (
            // it is a static elements
            // eslint-disable-next-line react/no-array-index-key
            <li key={index} className={s.centeredListItem}>
              {node}
            </li>
          ))}
        <li className={s.centeredListItem} id={LANGUAGE_POPUP_ID}>
          <LanguagePopup />
        </li>
      </ul>
      {isThemeAvailable && (
        <ThemeButton onClick={onThemeToggle} currentTheme={currentTheme} />
      )}
      <nav className={s.nav}>
        <ul className={s.list}>
          <li className={cn(s.listItem, s.listItemFullscreen)}>
            {screenfull.isEnabled && (
              <button
                type="button"
                className={s.customButton}
                onClick={handleFullscreenClick}
              >
                {isFullScreen ? (
                  <FullScreenExitIcon size={10} />
                ) : (
                  <FullScreenIcon size={10} />
                )}
              </button>
            )}
          </li>
          {news > 0 && (
            <li className={cn(s.listItem, s.listItemNews)}>
              <Link to={OVERVIEW_NEWS_PATH}>
                <Badge
                  type={BadgeType.Reverse}
                  value={news >= 100 ? '99+' : news}
                >
                  <BellIcon size={16} color="white" />
                </Badge>
              </Link>
            </li>
          )}
          <li className={cn(s.listItem, s.listItemBalance)}>
            <button
              className={s.customButton}
              onClick={onModeChange}
              type="button"
            >
              {showBalance && <div className={s.balance}>{currentBalance}</div>}
              {isPrivate ? <NotEyeIcon size={10} /> : <EyeIcon size={10} />}
            </button>
          </li>

          <li className={s.dropdown}>
            <DropdownMenu
              size={DropdownMenuSize.Large}
              placement="bottom-end"
              width="auto"
              palette={currentTheme}
              options={[
                ...dropdownItems,
                {
                  value: `${dropdownItems.length + 1}`,
                  label: 'Logout',
                  onSelect: logout,
                },
              ]}
            >
              {({ onToggle, ref, visible }) => (
                <button
                  qa-element="header-dropdown"
                  type="button"
                  ref={ref}
                  className={cn(s.customButton, s.user)}
                  onClick={onToggle}
                >
                  <span className={s.username}>
                    {isWideMobile ? nickname.slice(0, 1) : nickname}
                  </span>
                  {!isWideMobile &&
                    (visible ? (
                      <ArrowTipUpIcon size={10} />
                    ) : (
                      <ArrowTipDownIcon size={10} />
                    ))}
                </button>
              )}
            </DropdownMenu>
          </li>
        </ul>
      </nav>
    </header>
  );
};

const mapStateToProps = (state) => ({
  username: currentUserMaybeSelector(state)?.username,
  isSidebarShown: state.sidebar.isSidebarShown,
  isWideMobile: isWideMobileRootSelector(state),
});

export default connect(mapStateToProps, {
  expandSidebar: expandSidebarAction,
  collapseSidebar: collapseSidebarAction,
  logout: logoutAction,
})(Header);
